/**
 * meeting.helpers — pure helpers for the native meeting scheduler (Wave 4).
 *
 * Native replacement for the geocoding slice of the legacy `buildMeetingDTO`
 * (previously `legacyBridge/InstructionStateBridge.tsx:99-118`, deleted in the
 * Wave 4 teardown): extracts the key-pickup
 * `address` label + `coordinates` from a geocoded `ReactGeocodePlace` so the v2
 * wire (`serializePlacement` reads `meeting.keyPickup.coordinates`) keeps the
 * routable coordinates the plain-text-field revert dropped.
 *
 * Kept pure (no React / store / legacy provider) so the section stays testable
 * and the DTO shape stays the single source of truth.
 */

import type { ReactGeocodePlace } from 'react-geocode'

import { ProductKind } from 'constants/product'
import type { MeetingMode } from 'models/purchaseFlowRevamp'

/** Ordered meeting modes surfaced in the mode radio. */
export const MEETING_MODES: readonly MeetingMode[] = ['ON_SITE', 'KEY_PICKUP', 'THIRD_PARTY']

/**
 * Maps each meeting mode to the organization `ProductKind` it is keyed under in
 * `useInstructionCatalogue().organizationInstructions`. Every offerable mode is
 * catalogue-backed — `NONE` was a synthetic "no meeting needed" option and is no
 * longer offered: the step only appears when a product needs a creative on site,
 * so declining a meeting is never a valid answer here.
 */
export const MEETING_MODE_KIND: Partial<Record<MeetingMode, ProductKind>> = {
  ON_SITE: ProductKind.MEETING_ON_SITE,
  KEY_PICKUP: ProductKind.KEYS_PICKUP,
  THIRD_PARTY: ProductKind.ORGANIZATION_THIRD_PARTY,
}

/** Loading/ready/error status of the instruction catalogue (mirrors the hook). */
type CatalogueStatus = 'idle' | 'loading' | 'ready' | 'error'

/** The one field of an organization option this module decides on. */
interface FeeBearingOption {
  amount?: number
}

/**
 * Order the mode radio the way legacy did: cheapest first, with
 * third-party pinned to the top whatever it costs
 * (`InstructionListing.component.tsx` sorted on `feePrice`, then `unshift`ed
 * `ORGANIZATION_THIRD_PARTY`).
 *
 * v2 rendered a hardcoded literal instead, which both lost the fee ordering and
 * put third-party LAST — the cheapest and least committal option buried under
 * two that cost money.
 *
 * An option with no amount sorts as free, matching legacy (`bigFromFee` mapped
 * an absent fee to zero). The sort is stable, so equal fees keep `MEETING_MODES`
 * order — which is what makes the third-party pin the only deliberate override.
 */
export const sortMeetingModes = (
  modes: ReadonlyArray<MeetingMode>,
  organizationInstructions: Partial<Record<ProductKind, FeeBearingOption | unknown>>
): MeetingMode[] => {
  const feeOf = (mode: MeetingMode): number => {
    const option = organizationInstructions[MEETING_MODE_KIND[mode] as ProductKind] as FeeBearingOption | undefined
    return option?.amount ?? 0
  }

  return [...modes]
    .sort((a, b) => feeOf(a) - feeOf(b))
    .reduce<MeetingMode[]>((result, mode) => {
      if (mode === 'THIRD_PARTY') result.unshift(mode)
      else result.push(mode)
      return result
    }, [])
}

/** Why the offered meeting modes are narrower than the catalogue's. */
export type MeetingModeRestriction = 'none' | 'third-party-only' | 'no-third-party' | 'flexible'

/**
 * Resolve which meeting modes the mode radio should offer, driven by the
 * instruction catalogue rather than a hardcoded list (a user could otherwise
 * pick a mode the BE never offered → place-order rejection).
 *
 *   - `loading`/`idle`: return `[]` — the caller renders a spinner instead of
 *     flashing a possibly-wrong subset.
 *   - `error`: fall back to ALL modes — we can't know what the BE offers, and
 *     blocking the user from scheduling is worse than the rare over-offer.
 *   - `ready`: only the organization kinds actually present in the catalogue,
 *     plus synthetic `NONE`, plus the currently-selected mode (a resumed draft
 *     may hold a mode the catalogue has since dropped — keep it visible so the
 *     user can re-pick rather than see a blank radio group).
 *
 * `restriction` narrows the result whatever the catalogue offers, for the
 * reasons the catalogue cannot know about:
 *
 *   - `third-party-only` — a measurement product is in the cart. The BE does NOT
 *     narrow: `ConfigurationService` only swaps in the zero-price third-party
 *     variant and keeps offering on-site and key-pickup, while legacy dropped
 *     both client-side (`InstructionListing`'s `hasMeasurementProduct` gate). A
 *     technician books the appointment afterwards, so a client-chosen date — the
 *     thing those two modes collect — has nobody to bind to. Delete this case
 *     once the BE narrows the ORGANIZATION options itself.
 *   - `no-third-party` — the client chose a FIXED booking, i.e. they are naming
 *     the time themselves. Third-party means the creative arranges it, which is
 *     the opposite answer, and leaving it selectable would let the client set a
 *     time onto a mode that carries none — silently dropping it.
 *   - `flexible` — the creative arranges the time. Third party covers "call me"
 *     and "call someone else"; key pickup stays, because the keys can sit with a
 *     concierge while the creative arranges the time. On-site goes:
 *     a flexible on-site meeting is the third-party card.
 *
 * The restriction is applied for `error` too: the cart shape is known locally,
 * so failing open to all modes would re-offer exactly what must not be offered.
 * If the catalogue is `ready` and does not advertise third-party at all the
 * result is empty — a catalogue misconfiguration worth surfacing rather than
 * papering over with a mode the place-order call would reject.
 */
export const resolveMeetingModes = (
  organizationInstructions: Partial<Record<ProductKind, unknown>>,
  status: CatalogueStatus,
  selectedMode?: MeetingMode,
  restriction: MeetingModeRestriction = 'none'
): MeetingMode[] => {
  if (status === 'loading' || status === 'idle') return []

  const offered = MEETING_MODES.filter((mode) => {
    if (restriction === 'third-party-only') return mode === 'THIRD_PARTY'
    if (restriction === 'no-third-party') return mode !== 'THIRD_PARTY'
    if (restriction === 'flexible') return mode === 'THIRD_PARTY' || mode === 'KEY_PICKUP'
    return true
  })

  if (status === 'error') return sortMeetingModes(offered, organizationInstructions)

  return sortMeetingModes(
    offered.filter(
      (mode) =>
        mode === selectedMode ||
        !!organizationInstructions[MEETING_MODE_KIND[mode] as ProductKind]
    ),
    organizationInstructions
  )
}

/**
 * Maps a geocoded `ReactGeocodePlace` to the key-pickup DTO fields. Returns an
 * empty object for a null place (user cleared the address).
 */
export const placeToKeyPickupFields = (
  place: ReactGeocodePlace | null
): { address?: string; coordinates?: { lat: number; lng: number } } => {
  if (!place) return {}
  const loc = place.geometry?.location
  return {
    address: place.formatted_address || undefined,
    coordinates: loc ? { lat: loc.lat, lng: loc.lng } : undefined,
  }
}
